import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Map report type values to display names
const REPORT_TYPE_LABELS = {
  'project_summary': 'Project Summary',
  'blockers': 'Blockers',
  'velocity': 'Velocity',
  'custom': 'Custom',
};

/**
 * Check if a value is a UUID
 */
const isUUID = (value) => {
  if (typeof value !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Check if a key should be excluded (UUID fields)
 */
const shouldExcludeKey = (key) => {
  const excludeKeys = ['user_id', 'userId', 'id', 'project_id', 'projectId'];
  return excludeKeys.includes(key.toLowerCase().replace(/_/g, ''));
};

/**
 * Check if a value should be treated as numeric for styling
 */
const isNumericValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return true;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^-?\d+(\.\d+)?%?$/.test(trimmed);
};

/**
 * Remove UUID fields from data recursively
 */
export const removeUUIDFields = (data) => {
  if (data === null || data === undefined) return data;
  if (typeof data !== 'object') return isUUID(data) ? null : data;
  
  if (Array.isArray(data)) {
    return data
      .map(item => removeUUIDFields(item))
      .filter(item => item !== null);
  }
  
  const cleaned = {};
  Object.entries(data).forEach(([key, value]) => {
    if (shouldExcludeKey(key)) return;
    if (isUUID(value)) return;
    
    const cleanedValue = removeUUIDFields(value);
    if (cleanedValue !== null && cleanedValue !== undefined) {
      cleaned[key] = cleanedValue;
    }
  });
  
  return cleaned;
};

/**
 * Process report data: remove project_id and UUIDs, add project_name
 */
export const processReportData = (reportData) => {
  const cleaned = removeUUIDFields(reportData);
  return {
    project_name: reportData.project_name || 'N/A',
    ...cleaned
  };
};

/**
 * Format report data as styled HTML with beautified output
 */
export const formatReportAsText = (data, level = 0, parentIndent = 0) => {
  if (data === null || data === undefined) return '';
  
  const lines = [];
  const indentUnit = 8;
  const nestedIndentOffset = 4;
  const contentIndent = parentIndent + indentUnit + (level > 0 ? nestedIndentOffset : 0);
  const headerIndent = contentIndent;
  
  // Handle primitive values
  if (typeof data !== 'object') {
    if (isUUID(data)) return '';
    const valueClass = isNumericValue(data) ? 'report-value-emphasis' : 'report-value';
    return `<span class="${valueClass}">${String(data)}</span>`;
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) return '<span class="report-empty">None</span>';
    
    data.forEach((item, i) => {
      if (item !== null && typeof item === 'object') {
        const itemLabel = `Item ${i + 1}`;
        const sectionClasses = [
          'report-section',
          `report-level-${level}`,
          level === 0 ? 'report-section-root' : '',
          Array.isArray(item) ? 'report-section-array' : 'report-section-object'
        ].filter(Boolean).join(' ');
        lines.push(`<div class="${sectionClasses}">`);
        lines.push(`<div class="report-section-header" style="padding-left: ${headerIndent}px">${itemLabel}</div>`);
        const nested = formatReportAsText(item, level + 1, contentIndent);
        if (nested) lines.push(`<div class="report-section-content" style="margin-left: ${contentIndent}px">${nested}</div>`);
        lines.push('</div>');
      } else if (!isUUID(item)) {
        lines.push(`<div class="report-bullet" style="margin-left: ${contentIndent}px">• ${item}</div>`);
      }
    });
    
    return lines.join('');
  }
  
  // Handle objects
  const entries = Object.entries(data).filter(([k, v]) => !shouldExcludeKey(k) && !isUUID(v));
  
  entries.forEach(([key, value], idx) => {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    
    if (value === null || value === undefined) {
      lines.push(`<div class="report-field report-level-${level}">`);
      lines.push(`<span class="report-label">${label}:</span> <span class="report-empty">N/A</span>`);
      lines.push('</div>');
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        const sectionClasses = [
          'report-section',
          `report-level-${level}`,
          level === 0 ? 'report-section-root' : '',
          'report-section-array'
        ].filter(Boolean).join(' ');
        lines.push(`<div class="${sectionClasses}">`);
        lines.push(
          `<div class="report-section-header report-section-header-inline" style="padding-left: ${headerIndent}px"><span class="report-section-heading">${label}</span>: <span class="report-section-empty-inline">None</span></div>`
        );
        lines.push('</div>');
      } else {
        const sectionClasses = [
          'report-section',
          `report-level-${level}`,
          level === 0 ? 'report-section-root' : '',
          'report-section-array'
        ].filter(Boolean).join(' ');
        lines.push(`<div class="${sectionClasses}">`);
        lines.push(`<div class="report-section-header" style="padding-left: ${headerIndent}px">${label}</div>`);
        const arrayContent = formatReportAsText(value, level + 1, contentIndent);
        if (arrayContent) lines.push(`<div class="report-section-content" style="margin-left: ${contentIndent}px">${arrayContent}</div>`);
        lines.push('</div>');
      }
    } else if (typeof value === 'object') {
      const sectionClasses = [
        'report-section',
        `report-level-${level}`,
        level === 0 ? 'report-section-root' : '',
        'report-section-object'
      ].filter(Boolean).join(' ');
      lines.push(`<div class="${sectionClasses}">`);
      lines.push(`<div class="report-section-header" style="padding-left: ${headerIndent}px">${label}</div>`);
      const nestedContent = formatReportAsText(value, level + 1, contentIndent);
      if (nestedContent) lines.push(`<div class="report-section-content" style="margin-left: ${contentIndent}px">${nestedContent}</div>`);
      lines.push('</div>');
    } else {
      if (level === 0 && key.toLowerCase().includes('name')) {
        lines.push(`<div class="report-field report-level-${level}">`);
        lines.push(`<strong class="report-label report-label-bold">${label}:</strong> <strong class="report-value-bold">${value}</strong>`);
        lines.push('</div>');
      } else {
        lines.push(`<div class="report-field report-level-${level}">`);
        const valueClass = isNumericValue(value) ? 'report-value-emphasis' : 'report-value';
        lines.push(`<span class="report-label">${label}:</span> <span class="${valueClass}">${value}</span>`);
        lines.push('</div>');
      }
    }
  });
  
  return lines.join('');
};

/**
 * Flatten nested JSON into level-wise key-value rows
 */
export const flattenToKeyValueRows = (data, prefix = '', depth = 0) => {
  const rows = [];
  const toLabel = (key) => String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (data === null || data === undefined) {
    rows.push({ name: prefix || '—', value: '—', depth });
    return rows;
  }
  if (typeof data !== 'object') {
    // Skip UUID values
    if (isUUID(data)) return rows;
    rows.push({ name: prefix || 'Value', value: String(data), depth });
    return rows;
  }

  if (Array.isArray(data)) {
    const nextDepth = depth + 1;
    data.forEach((item, i) => {
      if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
        Object.entries(item).forEach(([k, v]) => {
          // Skip UUID fields
          if (shouldExcludeKey(k) || isUUID(v)) return;
          const keyPath = prefix ? `${prefix} › Item ${i + 1} › ${toLabel(k)}` : `Item ${i + 1} › ${toLabel(k)}`;
          rows.push(...flattenToKeyValueRows(v, keyPath, nextDepth));
        });
      } else if (Array.isArray(item)) {
        rows.push(...flattenToKeyValueRows(item, prefix ? `${prefix} › ${i}` : String(i), nextDepth));
      } else {
        if (!isUUID(item)) {
          rows.push({ name: prefix ? `${prefix} › ${i}` : String(i), value: String(item ?? ''), depth: nextDepth });
        }
      }
    });
    return rows;
  }

  const nextDepth = depth + 1;
  Object.entries(data).forEach(([key, value]) => {
    // Skip UUID fields
    if (shouldExcludeKey(key) || isUUID(value)) return;
    
    const label = toLabel(key);
    const keyPath = prefix ? `${prefix} › ${label}` : label;
    rows.push(...flattenToKeyValueRows(value, keyPath, nextDepth));
  });
  return rows;
};

/**
 * Exports report content to PDF using html2canvas for text format
 * @param {Object} params - Export parameters
 * @param {string} params.content - HTML content to export
 * @param {string} params.reportType - Type of report
 * @param {string} params.format - Format of report (json, text, table)
 */
export const exportAgentReportToPDF = async ({ content, reportType, format }) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const maxWidth = pageWidth - (margin * 2);
  const lineHeight = 6;
  
  // Get display name for report type
  const reportTypeLabel = REPORT_TYPE_LABELS[reportType] || reportType;
  
  // Add title
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const title = `${reportTypeLabel} Report`;
  pdf.text(title, margin, margin + 10);
  
  // Add timestamp
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const timestamp = `Generated: ${new Date().toLocaleString()}`;
  pdf.text(timestamp, margin, margin + 18);
  
  if (format === 'text') {
    // Use html2canvas for text format to preserve formatting
    const reportElement = document.querySelector('.report-html-content');
    if (!reportElement) {
      pdf.setFontSize(12);
      pdf.setTextColor(255, 0, 0);
      pdf.text('Error: Report content not found', margin, margin + 35);
      pdf.save(`${reportType.toLowerCase().replace(/\s+/g, '-')}-report-${new Date().toISOString().split('T')[0]}.pdf`);
      return;
    }
    
    try {
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const firstPageHeight = pageHeight - margin * 2 - 28 - 10;
      const nextPageHeight = pageHeight - margin * 2;
      let position = margin + 28;

      const reportRect = reportElement.getBoundingClientRect();
      const breakTargets = Array.from(
        reportElement.querySelectorAll('.report-section, .report-field, .report-array-item, .report-bullet')
      ).map((el) => {
        const rect = el.getBoundingClientRect();
        return Math.max(0, rect.top - reportRect.top + rect.height);
      });
      const scale = canvas.width / reportRect.width;
      const breakpoints = breakTargets
        .filter((val) => Number.isFinite(val))
        .map((val) => Math.round(val * scale))
        .sort((a, b) => a - b);
      const minSlicePx = Math.round(16 * scale);
      let sourceY = 0;
      let isFirstPage = true;
      while (sourceY < canvas.height - 1) {
        const pageContentHeight = isFirstPage ? firstPageHeight : nextPageHeight;
        const pageHeightPx = (pageContentHeight / imgHeight) * canvas.height;
        const maxY = sourceY + pageHeightPx;
        let breakY = null;
        for (let i = breakpoints.length - 1; i >= 0; i -= 1) {
          const bp = breakpoints[i];
          if (bp > sourceY && bp <= maxY) {
            breakY = bp;
            break;
          }
        }
        if (!breakY || breakY <= sourceY) {
          breakY = Math.min(sourceY + pageHeightPx, canvas.height);
        }
        const sourceHeight = breakY - sourceY;
        if (sourceHeight <= 0 || sourceHeight < minSlicePx) break;
        if (!isFirstPage) {
          pdf.addPage();
          position = margin;
        }
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageHeightMm = (sourceHeight / canvas.height) * imgHeight;
        pdf.addImage(pageImgData, 'PNG', margin, position, imgWidth, pageHeightMm);

        sourceY += sourceHeight;
        isFirstPage = false;
      }
    } catch (error) {
      console.error('Error generating PDF with html2canvas:', error);
      pdf.setFontSize(12);
      pdf.setTextColor(255, 0, 0);
      pdf.text('Error generating PDF content', margin, margin + 35);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(error.message || 'Unknown error', margin, margin + 45);
    }
  } else {
    // For JSON format, use jsPDF text rendering
    pdf.setFontSize(10);
    pdf.setFont('courier', 'normal');
    let yPosition = margin + 28;
    const lines = pdf.splitTextToSize(content, maxWidth);
    lines.forEach((line) => {
      if (yPosition > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += lineHeight;
    });
  }
  
  // Save PDF
  const filename = `${reportType.toLowerCase().replace(/\s+/g, '-')}-report-${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
};
